import React, { Suspense, lazy, ReactElement, ComponentType } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import { adminRouteAbsolute } from './utils/constants/route';
import { StorageEnum } from './utils/enum';
import { marketingURL } from './utils/constants/env';
import { getRoleFromToken, isTokenExpired } from './utils/tokenUtils';
import { notifyRouteChanged } from './utils/immersiveMode';

/**
 * Wrapper around React.lazy that retries loading a chunk once on failure.
 * When a chunk fails to load (e.g. after a new deployment), it reloads the page
 * to get fresh assets. Uses sessionStorage to prevent infinite reload loops.
 */
function lazyWithRetry(importFn: () => Promise<{ default: ComponentType<any> }>) {
    return lazy(async () => {
        try {
            return await importFn();
        } catch (error: any) {
            // Only auto-reload for chunk load errors
            const isChunkError =
                error?.name === 'ChunkLoadError' ||
                error?.message?.includes('Loading chunk') ||
                error?.message?.includes("Unexpected token");

            if (isChunkError) {
                const reloadKey = 'chunk_retry_reload';
                const lastReload = sessionStorage.getItem(reloadKey);
                const now = Date.now();

                // Only reload once within a 10-second window to prevent loops
                if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
                    sessionStorage.setItem(reloadKey, now.toString());
                    window.location.reload();
                    // Return a never-resolving promise to prevent React from rendering before reload
                    return new Promise(() => {});
                }
            }

            // Re-throw if not a chunk error or if we already retried
            throw error;
        }
    });
}

// Lazy loading with auto-retry on chunk errors
const Login = lazyWithRetry(() => import('./pages/Auth/Login'));
const Register = lazyWithRetry(() => import('./pages/Auth/Register'));
const OrganizationSignUp = lazyWithRetry(() => import('./pages/Auth/OrganizationSignUp'));
const VerifyAccount = lazyWithRetry(() => import('./pages/Auth/VerifyAccount'));
const WelcomeOnboarding = lazyWithRetry(() => import('./pages/Auth/WelcomeOnboarding'));
const TermsAndConditions = lazyWithRetry(() => import('./pages/Auth/TermsAndConditions'));
const ForgotPassword = lazyWithRetry(() => import('./pages/Auth/ForgotPassword'));
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard/AdminDashboard'));
const StudentDashboard = lazyWithRetry(() => import('./pages/Dashboard/StudentDashboard'));
const StudentLessonViewer = lazyWithRetry(() => import('./pages/StudentPortal/StudentLessonViewer'));
const StudentQuizWizard = lazyWithRetry(() => import('./pages/StudentPortal/StudentQuizWizard'));
const StudentFlashcardViewer = lazyWithRetry(() => import('./pages/StudentPortal/StudentFlashcardViewer'));
const Profile = lazyWithRetry(() => import('./pages/Auth/Profile'));
const StudentClassList = lazyWithRetry(() => import('./pages/StudentPortal/StudentClassList'));
const StudentAssignmentList = lazyWithRetry(() => import('./pages/StudentPortal/StudentAssignmentList'));
const StudentLessonList = lazyWithRetry(() => import('./pages/StudentPortal/StudentLessonList'));
const WebCard = lazyWithRetry(() => import('./pages/Auth/WebCard'));
const CreditsPage = lazyWithRetry(() => import('./pages/Credits/CreditsPage'));
const PricingPage = lazyWithRetry(() => import('./pages/Pricing/PricingPage'));
const FinanceDashboard = lazyWithRetry(() => import('./pages/Finance/FinanceDashboard'));
const AttendanceOverview = lazyWithRetry(() => import('./pages/Attendance/AttendanceOverview'));
const OrgStaffAttendance = lazyWithRetry(() => import('./pages/Attendance/OrgStaffAttendance'));
const StudentTuitionPortal = lazyWithRetry(() => import('./pages/StudentPortal/StudentTuitionPortal'));

// Class Management
const Classes = lazyWithRetry(() => import('./pages/Classes/ClassList'));
const AddClass = lazyWithRetry(() => import('./pages/Classes/AddClass'));
const ClassDetail = lazyWithRetry(() => import('./pages/Classes/ClassDetail'));

// Lesson Categories
const CategoryList = lazyWithRetry(() => import('./pages/LessonCategories/CategoryList'));
const AddCategory = lazyWithRetry(() => import('./pages/LessonCategories/AddCategory'));
const EditCategory = lazyWithRetry(() => import('./pages/LessonCategories/EditCategory'));

// Posts
const PostCategories = lazyWithRetry(() => import('./pages/Posts/PostCategories'));
const Tags = lazyWithRetry(() => import('./pages/Posts/Tags'));
const Posts = lazyWithRetry(() => import('./pages/Posts/Posts'));
const PostForm = lazyWithRetry(() => import('./pages/Posts/PostForm'));
const PostPreview = lazyWithRetry(() => import('./pages/Posts/PostPreview'));

// Lessons
const Lessons = lazyWithRetry(() => import('./pages/Lessons/LessonList'));
const AddLesson = lazyWithRetry(() => import('./pages/Lessons/AddLesson'));
const LessonDetail = lazyWithRetry(() => import('./pages/Lessons/LessonDetail'));
const ResourceLessonDetail = lazyWithRetry(() => import('./pages/Lessons/ResourceLessonDetail'));

// Games
const GameTemplates = lazyWithRetry(() => import('./pages/Admin/GameTemplates'));

// Student Management
const Students = lazyWithRetry(() => import('./pages/Students/StudentList'));
const AddStudent = lazyWithRetry(() => import('./pages/Students/AddStudent'));
const EditStudent = lazyWithRetry(() => import('./pages/Students/EditStudent'));
const StudentDetail = lazyWithRetry(() => import('./pages/Students/StudentDetail'));

// Teacher Management
const Admins = lazyWithRetry(() => import('./pages/Admins/AdminList'));
const AddAdmin = lazyWithRetry(() => import('./pages/Admins/AddAdmin'));
const EditAdmin = lazyWithRetry(() => import('./pages/Admins/EditAdmin'));

const Sales = lazyWithRetry(() => import('./pages/Sales/SaleList'));
const AddSale = lazyWithRetry(() => import('./pages/Sales/AddSale'));
const EditSale = lazyWithRetry(() => import('./pages/Sales/EditSale'));

const Teachers = lazyWithRetry(() => import('./pages/Teachers/TeacherList'));
const AddTeacher = lazyWithRetry(() => import('./pages/Teachers/AddTeacher'));
const EditTeacher = lazyWithRetry(() => import('./pages/Teachers/EditTeacher'));
const LessonManagement = lazyWithRetry(() => import('./pages/Lessons/LessonList'));
const GradingDetail = lazyWithRetry(() => import('./pages/Teachers/GradingDetail'));
const SubmissionList = lazyWithRetry(() => import('./pages/Teachers/SubmissionList'));
const TeacherResources = lazyWithRetry(() => import('./pages/Teachers/TeacherResources'));

// Parent Management
const Parents = lazyWithRetry(() => import('./pages/Parents/ParentList'));
const AddParent = lazyWithRetry(() => import('./pages/Parents/AddParent'));
const EditParent = lazyWithRetry(() => import('./pages/Parents/EditParent'));
const ParentDashboard = lazyWithRetry(() => import('./pages/Dashboard/ParentDashboard'));
const ContactList = lazyWithRetry(() => import('./pages/Contacts/ContactList'));
const UserManagement = lazyWithRetry(() => import('./pages/Admin/UserManagement'));
const UserDetail = lazyWithRetry(() => import('./pages/Admin/UserDetail'));
const ActivityLogs = lazyWithRetry(() => import('./pages/Admin/ActivityLogs'));
const AdminCreditHistory = lazyWithRetry(() => import('./pages/Admin/AdminCreditHistory'));
const OrganizationDetail = lazyWithRetry(() => import('./pages/Admin/OrganizationDetail'));
const OrganizationManagement = lazyWithRetry(() => import('./pages/Admin/OrganizationManagement'));
const ComponentManagement = lazyWithRetry(() => import('./pages/Admin/ComponentManagement'));
const PlanManagement = lazyWithRetry(() => import('./pages/Admin/PlanManagement'));
const SubscriptionManagement = lazyWithRetry(() => import('./pages/Admin/SubscriptionManagement'));
const TGOKnowledgeManagement = lazyWithRetry(() => import('./pages/Admin/TGOKnowledgeManagement'));

const WhiteboardPage = lazyWithRetry(() => import('./pages/Whiteboard/WhiteboardPage'));
const WorksheetPage = lazyWithRetry(() => import('./pages/Worksheet/WorksheetPage'));
const StudentWorksheetPage = lazyWithRetry(() => import('./pages/Worksheet/StudentWorksheetPage'));
const TimetablePage = lazyWithRetry(() => import('./pages/Timetable/TimetablePage'));
const ArcadeList = lazyWithRetry(() => import('./pages/Arcade/ArcadeList'));
const ArcadePlayer = lazyWithRetry(() => import('./pages/Arcade/ArcadePlayer'));
const TiGOChatWidget = lazyWithRetry(() => import('./components/TigoAssistant/TiGOChatWidget'));

// Notifications
const NotificationsPage = lazyWithRetry(() => import('./pages/Notifications/Notifications'));
const PushNotifications = lazyWithRetry(() => import('./pages/PushNotifications/PushNotifications'));

// Organization Management
const OrganizationProfile = lazyWithRetry(() => import('./pages/Organization/OrganizationProfile'));
const OrganizationMembers = lazyWithRetry(() => import('./pages/Organization/OrganizationMembers'));
const OrganizationPosts = lazyWithRetry(() => import('./pages/Organization/OrganizationPosts'));
const OrganizationPostEditor = lazyWithRetry(() => import('./pages/Organization/OrganizationPostEditor'));
const OrganizationLeads = lazyWithRetry(() => import('./pages/Organization/OrganizationLeads'));
const OrganizationSurveys = lazyWithRetry(() => import('./pages/Organization/OrganizationSurveyManagement'));
const PayrollManagement = lazyWithRetry(() => import('./pages/Payroll/PayrollManagement'));
const SlideRemoteController = lazyWithRetry(() => import('./pages/SlideRemote/SlideRemoteController'));


const ProtectedRoute = ({ children, allowedRoles }: { children: ReactElement, allowedRoles?: string[] }): ReactElement => {
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    // Read role from server-signed JWT token (tamper-proof)
    const role = getRoleFromToken(token);
    const location = useLocation();

    if (!token || isTokenExpired(token)) {
        return <Navigate to={adminRouteAbsolute.login} replace />;
    }

    // Redirect unverified users to verify-account
    const storedUser = localStorage.getItem(StorageEnum.USER_INFO);
    if (storedUser) {
        try {
            const userInfo = JSON.parse(storedUser);
            if (userInfo && (userInfo.is_verified === false || userInfo.pending_activation === true)) {
                return <Navigate to={`/verify-account?email=${encodeURIComponent(userInfo.email || '')}`} replace />;
            }
        } catch (e) {
            // ignore JSON parse error
        }
    }

    // Redirect UNASSIGNED users to welcome page
    if (role === 'UNASSIGNED' && location.pathname !== '/welcome') {
        return <Navigate to="/welcome" replace />;
    }

    if (allowedRoles && role && !allowedRoles.includes(role)) {
        if (location.pathname === '/finance') {
            if (role === 'STUDENT' || role === 'PARENT') return <Navigate to="/student-portal" replace />;
            if (role === 'ORG_SALES' || role === 'TEACHER') return <Navigate to="/" replace />;
        }
        if (role === 'STUDENT') return <Navigate to="/student-portal" replace />;
        if (role === 'PARENT') return <Navigate to="/parent" replace />;
        return <Navigate to={adminRouteAbsolute.login} replace />; 
    }

    return children;
};

const PublicRoute = ({ children }: { children: ReactElement }): ReactElement => {
    const token = localStorage.getItem(StorageEnum.ACCESS_TOKEN);
    const role = getRoleFromToken(token);
    const location = useLocation();

    if (token) {
        if (isTokenExpired(token)) {
            localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
            localStorage.removeItem(StorageEnum.USER_INFO);
            localStorage.removeItem(StorageEnum.USER_ROLE);
            localStorage.removeItem(StorageEnum.PARENT_ACCESS_TOKEN);
            return children;
        }
        const storedUser = localStorage.getItem(StorageEnum.USER_INFO);
        if (storedUser) {
            try {
                const userInfo = JSON.parse(storedUser);
                if (userInfo && (userInfo.is_verified === false || userInfo.pending_activation === true)) {
                    return <Navigate to={`/verify-account?email=${encodeURIComponent(userInfo.email || '')}`} replace />;
                }
            } catch (e) {
                // ignore
            }
        }
        const getCookie = (name: string) => {
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2) return decodeURIComponent(parts.pop()?.split(';').shift() || '');
            return null;
        };
        const redirectUrlCookie = getCookie('lms_redirect_url');
        
        if (redirectUrlCookie) {
            const hostname = window.location.hostname;
            const domainParts = hostname.split('.');
            let domainStr = '';
            if (domainParts.length > 2) {
                domainStr = `domain=.${domainParts.slice(-2).join('.')};`;
            } else if (hostname !== 'localhost') {
                domainStr = `domain=.${hostname};`;
            }
            
            document.cookie = `lms_redirect_url=; ${domainStr} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
            document.cookie = `lms_redirect_url=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
            
            window.location.href = redirectUrlCookie;
            return <></>;
        }

        const params = new URLSearchParams(location.search);
        const redirectUrl = params.get('redirect');
        if (redirectUrl) {
            window.location.href = redirectUrl;
            return <></>;
        }
        if (role === 'STUDENT') return <Navigate to="/student" replace />;
        if (role === 'PARENT') return <Navigate to="/parent" replace />;
        if (role === 'UNASSIGNED') return <Navigate to="/welcome" replace />;
        return <Navigate to={adminRouteAbsolute.dashboard} replace />;
    }
    return children;
};

const PageTracker = () => {
    const location = useLocation();

    React.useEffect(() => {
        notifyRouteChanged();
        const isProduction = (process.env.NODE_ENV === 'production' || (window as any).REACT_APP_ENV === 'production') &&
            typeof window !== 'undefined' &&
            window.location.hostname !== 'localhost' &&
            window.location.hostname !== '127.0.0.1' &&
            !window.location.hostname.includes('dev.') &&
            !window.location.hostname.includes('staging.') &&
            (window.location.hostname === 'app.trogiang.online' || window.location.hostname.endsWith('.trogiang.online'));

        if (isProduction && typeof (window as any).gtag === 'function') {
            (window as any).gtag('config', 'G-XTT6518NH5', {
                page_path: location.pathname + location.search,
            });
        }
    }, [location]);

    return null;
};

const MarketingRedirect: React.FC = () => {
    const location = useLocation();
    React.useEffect(() => {
        const targetUrl = `${marketingURL}${location.pathname}${location.search}${location.hash}`;
        window.location.replace(targetUrl);
    }, [location]);
    return null;
};

export const AppRouter = () => {
    React.useEffect(() => {
        if (typeof window !== 'undefined') {
            const host = window.location.hostname;
            // If user opened a subdomain on port 3000 (e.g. demo-sub.localhost:3000), redirect to marketing port 3002
            if (window.location.port === '3000' && host.endsWith('.localhost') && host !== 'localhost') {
                const port3002Url = `http://${host}:3002${window.location.pathname}${window.location.search}${window.location.hash}`;
                window.location.replace(port3002Url);
            }
        }
    }, []);

    return (
        <BrowserRouter>
            <NavigationGuardProvider>
                <PageTracker />
                <Suspense fallback={<div>Loading pages...</div>}>
                <Routes>
                    {/* Redirect public org / portal routes to marketing app */}
                    <Route path="/org/*" element={<MarketingRedirect />} />
                    <Route path="/c/*" element={<MarketingRedirect />} />

                    {/* Redirect root / to login */}
                    <Route
                        path="/"
                        element={<Navigate to={adminRouteAbsolute.login} replace />}
                    />

                    {/* Public Routes */}
                    <Route
                        path={adminRouteAbsolute.login}
                        element={
                            <PublicRoute>
                                <Login />
                            </PublicRoute>
                        }
                    />
                    
                    <Route
                        path="/register"
                        element={
                            <PublicRoute>
                                <Register />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/organization/sign-up"
                        element={
                            <PublicRoute>
                                <OrganizationSignUp />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/verify-account"
                        element={
                            <PublicRoute>
                                <VerifyAccount />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/forgot-password"
                        element={
                            <PublicRoute>
                                <ForgotPassword />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/welcome"
                        element={
                            <ProtectedRoute>
                                <WelcomeOnboarding />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/terms" element={<TermsAndConditions />} />

                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute>
                                <Profile />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/profile/:referalCode"
                        element={
                            <WebCard />
                        }
                    />

                    {/* All logged-in users */}
                    <Route
                        path="/credits"
                        element={
                            <ProtectedRoute>
                                <CreditsPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/pricing"
                        element={
                            <ProtectedRoute>
                                <PricingPage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/notifications"
                        element={
                            <ProtectedRoute>
                                <NotificationsPage />
                            </ProtectedRoute>
                        } 
                    />
                    <Route 
                        path="/push-notifications" 
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <PushNotifications />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected Routes - ADMIN/TEACHER/COMPANY_ADMIN */}
                    <Route
                        path={adminRouteAbsolute.dashboard}
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'SALES', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />

                    {/* Organization Routes */}
                    <Route
                        path="/organization"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/profile"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/members"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationMembers />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/posts"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationPosts />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/posts/create"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationPostEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/posts/:postId/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationPostEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/leads"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationLeads />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/surveys"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrganizationSurveys />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/payroll"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <PayrollManagement />
                            </ProtectedRoute>
                        }
                    />



                    <Route
                        path="/students"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES', 'SALES']}>
                                <Students />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/students/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <AddStudent />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/students/:id/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <EditStudent />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/students/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES', 'SALES']}>
                                <StudentDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/teacher/lessons"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'COMPANY_ADMIN']}>
                                <LessonManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/teacher/whiteboard"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'COMPANY_ADMIN']}>
                                <WhiteboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teacher/worksheet/:exerciseId"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <WorksheetPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teacher/timetable/:classId"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <TimetablePage />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/lessons/resources"
                        element={
                            <ProtectedRoute>
                                <TeacherResources />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/teacher/grading/:submissionId"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'COMPANY_ADMIN']}>
                                <GradingDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/teacher/submissions"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'COMPANY_ADMIN']}>
                                <SubmissionList />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/classes"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <Classes />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'COMPANY_ADMIN', 'PARENT', 'ORG_SALES']}>
                                <AddClass />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/classes/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'STUDENT', 'PARENT', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <ClassDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/lessons"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'STUDENT', 'COMPANY_ADMIN']}>
                                <Lessons />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lessons/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <AddLesson />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lessons/resources/:id"
                        element={
                            <ProtectedRoute>
                                <ResourceLessonDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lessons/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'STUDENT', 'COMPANY_ADMIN']}>
                                <LessonDetail />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/lesson-categories"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <CategoryList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lesson-categories/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <AddCategory />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/lesson-categories/edit/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'TEACHER', 'PARENT', 'COMPANY_ADMIN']}>
                                <EditCategory />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected Routes - ADMIN ONLY */}
                    <Route
                        path="/admin/post-categories"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <PostCategories />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/tags"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <Tags />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/posts"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <Posts />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/posts/create"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <PostForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/posts/edit/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <PostForm />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/posts/:slug"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <PostPreview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <UserManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/users/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <UserDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <UserDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/finance"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'SALES']}>
                                <FinanceDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/attendance"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'TEACHER', 'ORG_SALES', 'SALES']}>
                                <AttendanceOverview />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/organization/attendance"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'COMPANY_ADMIN', 'ORG_SALES']}>
                                <OrgStaffAttendance />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/organization/attendance"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrgStaffAttendance />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/organizations"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrganizationManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/components"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <ComponentManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/surveys"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrganizationSurveys />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/plans"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <PlanManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/subscriptions"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <SubscriptionManagement />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/tgo-knowledge"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <TGOKnowledgeManagement />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admin/organizations/:id"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrganizationDetail />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/organizations/:id/posts/create"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrganizationPostEditor />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/organizations/:id/posts/:postId/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <OrganizationPostEditor />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/admins"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <Admins />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admins/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <AddAdmin />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admins/:id/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <EditAdmin />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <Sales />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <AddSale />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/sales/:id/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <EditSale />
                            </ProtectedRoute>
                        }
                    />
                    
                    <Route
                        path="/teachers"

                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <Teachers />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teachers/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <AddTeacher />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/games"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <GameTemplates />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/logs"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <ActivityLogs />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/credits/history"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <AdminCreditHistory />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/teachers/:id/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <EditTeacher />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected Routes - ADMIN ONLY (Parents Mgmt) */}
                    <Route
                        path="/parents"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <Parents />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/parents/new"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <AddParent />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/parents/:id/edit"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN']}>
                                <EditParent />
                            </ProtectedRoute>
                        }
                    />

                    <Route
                        path="/contacts"
                        element={
                            <ProtectedRoute allowedRoles={['ADMIN', 'SALES']}>
                                <ContactList />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected Routes - PARENT */}
                    <Route
                        path="/parent"
                        element={
                            <ProtectedRoute allowedRoles={['PARENT']}>
                                <ParentDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/parent/submissions"
                        element={
                            <ProtectedRoute allowedRoles={['PARENT']}>
                                <SubmissionList />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/parent/grading/:submissionId"
                        element={
                            <ProtectedRoute allowedRoles={['PARENT']}>
                                <GradingDetail />
                            </ProtectedRoute>
                        }
                    />

                    {/* Protected Routes - STUDENT */}
                    <Route
                        path="/student"
                        element={
                            <ProtectedRoute allowedRoles={['STUDENT']}>
                                <StudentDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/student/lessons/:id" element={<StudentLessonViewer />} />
                    <Route path="/student/assignments/:id" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentQuizWizard />
                        </ProtectedRoute>
                    } />
                    <Route path="/worksheet/:exerciseId" element={<StudentWorksheetPage />} />
                    <Route path="/student/worksheet/:exerciseId" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentWorksheetPage />
                        </ProtectedRoute>
                    } />
                    <Route path="/timetable/:classId" element={<TimetablePage />} />
                    <Route path="/student/timetable/:classId" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <TimetablePage />
                        </ProtectedRoute>
                    } />
                    <Route path="/student/flashcards/:id" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentFlashcardViewer />
                        </ProtectedRoute>
                    } />
                    <Route path="/student/classes" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentClassList />
                        </ProtectedRoute>
                    } />
                    <Route path="/student/assignments" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentAssignmentList />
                        </ProtectedRoute>
                    } />
                    <Route path="/student/lessons" element={
                        <ProtectedRoute allowedRoles={['STUDENT']}>
                            <StudentLessonList />
                        </ProtectedRoute>
                    } />

                    {/* Student & Parent Tuition Portal */}
                    <Route
                        path="/student-portal"
                        element={
                            <ProtectedRoute allowedRoles={['STUDENT', 'PARENT', 'ADMIN', 'COMPANY_ADMIN', 'ORG_SALES', 'SALES']}>
                                <StudentTuitionPortal />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/student/tuition"
                        element={
                            <ProtectedRoute allowedRoles={['STUDENT', 'PARENT', 'ADMIN', 'COMPANY_ADMIN', 'ORG_SALES', 'SALES']}>
                                <StudentTuitionPortal />
                            </ProtectedRoute>
                        }
                    />

                    {/* Arcade Games / Game Học Đường */}
                    <Route path="/arcade" element={<ArcadeList />} />
                    <Route path="/arcade/play/:slug" element={<ArcadePlayer />} />
                    <Route path="/student/arcade" element={
                        <ProtectedRoute allowedRoles={['STUDENT', 'TEACHER', 'ADMIN', 'PARENT']}>
                            <ArcadeList />
                        </ProtectedRoute>
                    } />
                    <Route path="/student/arcade/play/:slug" element={
                        <ProtectedRoute allowedRoles={['STUDENT', 'TEACHER', 'ADMIN', 'PARENT']}>
                            <ArcadePlayer />
                        </ProtectedRoute>
                    } />

                    {/* Slide Remote Control */}
                    <Route path="/slides/remote/:sessionId" element={<SlideRemoteController />} />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to={adminRouteAbsolute.login} replace />} />
                </Routes>
            </Suspense>
            <Suspense fallback={null}>
                <TiGOChatWidget />
            </Suspense>
            </NavigationGuardProvider>
        </BrowserRouter>
    );
};
